import { useState } from 'react';
import {
    GitMerge, Lock, Crown, Clock, Shield, ChevronDown,
    Square, Eye, Zap, ArrowLeft, CheckCircle, XCircle,
    AlertTriangle, Activity, Layers, Brain, Sparkles
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useReplica } from '../contexts/ReplicaContext';

interface CognitiveMergeViewProps {
    onNavigate: (view: string) => void;
}

// ─── Types (mirrors backend cognitive-merge.service.ts) ──────────────
type MergeStatus =
    | 'pending_consent'
    | 'consent_granted'
    | 'configuring'
    | 'active'
    | 'separating'
    | 'completed'
    | 'rejected'
    | 'expired'
    | 'emergency_stop';

interface MergeSession {
    id: string;
    initiatorName: string;
    participants: { name: string; replicaId: string; consent: 'approved' | 'rejected' | 'pending' }[];
    status: MergeStatus;
    taskDescription: string;
    maxDurationMinutes: number;
    startedAt: string | null;
    expiresAt: string;
    nodesInherited: number;
    actionsExecuted: number;
    governanceHealth: number;
}

// ─── Demo Data ───────────────────────────────────────────────────────
const DEMO_ACTIVE_MERGES: MergeSession[] = [
    {
        id: 'merge-001',
        initiatorName: 'LUX',
        participants: [
            { name: 'LUX', replicaId: 'rep-1', consent: 'approved' },
            { name: 'ARIA', replicaId: 'rep-2', consent: 'approved' },
        ],
        status: 'active',
        taskDescription: 'Collaborative quarterly report analysis combining sales data expertise with financial modeling',
        maxDurationMinutes: 120,
        startedAt: new Date(Date.now() - 45 * 60000).toISOString(),
        expiresAt: new Date(Date.now() + 75 * 60000).toISOString(),
        nodesInherited: 47,
        actionsExecuted: 12,
        governanceHealth: 100,
    },
];

const DEMO_HISTORY: MergeSession[] = [
    {
        id: 'merge-old-1',
        initiatorName: 'LUX',
        participants: [
            { name: 'LUX', replicaId: 'rep-1', consent: 'approved' },
            { name: 'BOLT', replicaId: 'rep-3', consent: 'approved' },
        ],
        status: 'completed',
        taskDescription: 'Cross-functional customer support analysis with technical troubleshooting',
        maxDurationMinutes: 60,
        startedAt: '2026-03-20T14:00:00Z',
        expiresAt: '2026-03-20T15:00:00Z',
        nodesInherited: 32,
        actionsExecuted: 8,
        governanceHealth: 100,
    },
    {
        id: 'merge-old-2',
        initiatorName: 'ARIA',
        participants: [
            { name: 'ARIA', replicaId: 'rep-2', consent: 'approved' },
            { name: 'LUX', replicaId: 'rep-1', consent: 'rejected' },
        ],
        status: 'rejected',
        taskDescription: 'Proposed merge for marketing campaign strategy',
        maxDurationMinutes: 180,
        startedAt: null,
        expiresAt: '2026-03-18T12:00:00Z',
        nodesInherited: 0,
        actionsExecuted: 0,
        governanceHealth: 100,
    },
];

const DEMO_INCOMING: MergeSession[] = [
    {
        id: 'merge-req-1',
        initiatorName: 'NOVA',
        participants: [
            { name: 'NOVA', replicaId: 'rep-5', consent: 'approved' },
            { name: 'LUX', replicaId: 'rep-1', consent: 'pending' },
        ],
        status: 'pending_consent',
        taskDescription: 'Joint research document compilation for investor presentation',
        maxDurationMinutes: 90,
        startedAt: null,
        expiresAt: new Date(Date.now() + 24 * 3600000).toISOString(),
        nodesInherited: 0,
        actionsExecuted: 0,
        governanceHealth: 100,
    },
];

// ─── Status Helpers ──────────────────────────────────────────────────
const statusConfig: Record<MergeStatus, { label: string; color: string; bg: string; icon: typeof Activity }> = {
    pending_consent: { label: 'Awaiting Consent', color: 'text-amber-400', bg: 'bg-amber-500/10', icon: Clock },
    consent_granted: { label: 'Ready to Activate', color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: CheckCircle },
    configuring: { label: 'Configuring', color: 'text-blue-400', bg: 'bg-blue-500/10', icon: Layers },
    active: { label: 'Active Merge', color: 'text-cyan-400', bg: 'bg-cyan-500/10', icon: Activity },
    separating: { label: 'Separating', color: 'text-purple-400', bg: 'bg-purple-500/10', icon: Layers },
    completed: { label: 'Completed', color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: CheckCircle },
    rejected: { label: 'Rejected', color: 'text-red-400', bg: 'bg-red-500/10', icon: XCircle },
    expired: { label: 'Expired', color: 'text-hig-text-tertiary', bg: 'bg-hig-bg-tertiary', icon: Clock },
    emergency_stop: { label: 'Emergency Stop', color: 'text-red-400', bg: 'bg-red-500/10', icon: AlertTriangle },
};

export default function CognitiveMergeView({ onNavigate }: CognitiveMergeViewProps) {
    const { user } = useAuth();
    const { getUserReplicas } = useReplica();
    const allReplicas = user?.uid ? getUserReplicas(user.uid) : [];
    const [isPremium] = useState(true); // In production: check user subscription
    const [activeTab, setActiveTab] = useState<'active' | 'incoming' | 'history'>('active');
    const [expandedMerge, setExpandedMerge] = useState<string | null>(null);
    const [showInitiateModal, setShowInitiateModal] = useState(false);

    // Initiate form state
    const [mergeTask, setMergeTask] = useState('');
    const [mergeDuration, setMergeDuration] = useState(60);
    const [selectedReplicas, setSelectedReplicas] = useState<string[]>([]);
    const [shareMemory, setShareMemory] = useState(true);
    const [shareSkills, setShareSkills] = useState(true);
    const [shareIdentity, setShareIdentity] = useState(false);

    const formatTimeRemaining = (expiresAt: string) => {
        const diff = new Date(expiresAt).getTime() - Date.now();
        if (diff <= 0) return 'Expired';
        const mins = Math.floor(diff / 60000);
        const hrs = Math.floor(mins / 60);
        if (hrs > 0) return `${hrs}h ${mins % 60}m remaining`;
        return `${mins}m remaining`;
    };

    const formatDate = (iso: string) => {
        return new Date(iso).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    // ═══════════════════════════════════════════════════════════════════
    // PREMIUM GATE
    // ═══════════════════════════════════════════════════════════════════
    if (!isPremium) {
        return (
            <div className="max-w-4xl mx-auto pb-12">
                <div className="flex items-center gap-3 mb-8">
                    <button onClick={() => onNavigate('dashboard')}
                        className="p-2 rounded-lg text-hig-text-tertiary hover:text-hig-text-primary hover:bg-hig-bg-tertiary transition-colors">
                        <ArrowLeft size={18} />
                    </button>
                    <h1 className="text-2xl font-bold text-hig-text-primary tracking-tight">Cognitive Merge</h1>
                </div>

                {/* Premium Gate Card */}
                <div className="relative overflow-hidden rounded-3xl border border-purple-500/20 bg-gradient-to-br from-hig-bg-secondary via-purple-950/10 to-hig-bg-secondary">
                    {/* Decorative glow */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl pointer-events-none" />

                    <div className="relative p-12 text-center">
                        <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-purple-500/20 to-cyan-500/20 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-purple-500/10 relative">
                            <GitMerge size={36} className="text-purple-400" />
                            <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
                                <Lock size={14} className="text-white" />
                            </div>
                        </div>
                        <h2 className="text-3xl font-bold text-hig-text-primary mb-3">Cognitive Merge™</h2>
                        <p className="text-lg text-hig-text-secondary mb-2">Patent: HR-BREV-009</p>
                        <p className="text-base text-hig-text-secondary max-w-lg mx-auto mb-8 leading-relaxed">
                            Temporarily fuse two or more sovereign replicas into a unified cognitive context for collaborative task execution — with guaranteed separation afterward.
                        </p>

                        {/* Feature highlights */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10 max-w-2xl mx-auto">
                            <div className="bg-hig-bg-tertiary/50 rounded-xl p-4 border border-hig-divider">
                                <Brain size={20} className="text-purple-400 mx-auto mb-2" />
                                <p className="text-sm font-semibold text-hig-text-primary mb-1">Selective Inheritance</p>
                                <p className="text-xs text-hig-text-tertiary">Only task-relevant cognitive nodes are shared</p>
                            </div>
                            <div className="bg-hig-bg-tertiary/50 rounded-xl p-4 border border-hig-divider">
                                <Shield size={20} className="text-emerald-400 mx-auto mb-2" />
                                <p className="text-sm font-semibold text-hig-text-primary mb-1">Guaranteed Separation</p>
                                <p className="text-xs text-hig-text-tertiary">Zero residual data transfer after merge</p>
                            </div>
                            <div className="bg-hig-bg-tertiary/50 rounded-xl p-4 border border-hig-divider">
                                <Eye size={20} className="text-cyan-400 mx-auto mb-2" />
                                <p className="text-sm font-semibold text-hig-text-primary mb-1">Full Audit Trail</p>
                                <p className="text-xs text-hig-text-tertiary">Tamper-proof record of all merged actions</p>
                            </div>
                        </div>

                        <button onClick={() => onNavigate('pricing')}
                            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-600 to-cyan-600 text-white rounded-2xl font-bold text-base shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30 transition-all hover:scale-[1.02]">
                            <Crown size={18} />
                            Upgrade to Premium
                        </button>
                        <p className="text-xs text-hig-text-tertiary mt-3">Premium plan includes Cognitive Merge + all advanced features</p>
                    </div>
                </div>
            </div>
        );
    }

    // ═══════════════════════════════════════════════════════════════════
    // MERGE SESSION CARD
    // ═══════════════════════════════════════════════════════════════════
    const MergeCard = ({ merge, showActions = false }: { merge: MergeSession; showActions?: boolean }) => {
        const config = statusConfig[merge.status];
        const StatusIcon = config.icon;
        const isExpanded = expandedMerge === merge.id;

        return (
            <div className="border-b border-hig-divider last:border-b-0">
                <div className="p-5 hover:bg-hig-bg-elevated/30 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl ${config.bg} flex items-center justify-center`}>
                                <StatusIcon size={18} className={config.color} />
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-0.5">
                                    <p className="font-semibold text-hig-text-primary text-sm">{merge.taskDescription.slice(0, 60)}{merge.taskDescription.length > 60 ? '...' : ''}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${config.bg} ${config.color} border border-current/20 font-medium`}>
                                        {config.label}
                                    </span>
                                    <span className="text-xs text-hig-text-tertiary">
                                        {merge.participants.map(p => p.name).join(' + ')}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {merge.status === 'active' && (
                                <span className="text-xs text-cyan-400 font-medium flex items-center gap-1">
                                    <Clock size={12} />
                                    {formatTimeRemaining(merge.expiresAt)}
                                </span>
                            )}
                            {showActions && merge.status === 'pending_consent' && (
                                <div className="flex items-center gap-1.5">
                                    <button className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors flex items-center gap-1">
                                        <CheckCircle size={12} /> Approve
                                    </button>
                                    <button className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors flex items-center gap-1">
                                        <XCircle size={12} /> Reject
                                    </button>
                                </div>
                            )}
                            {merge.status === 'active' && (
                                <button className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors flex items-center gap-1">
                                    <Square size={12} /> Emergency Stop
                                </button>
                            )}
                            <button onClick={() => setExpandedMerge(isExpanded ? null : merge.id)}
                                className="p-1.5 text-hig-text-tertiary hover:text-hig-text-primary rounded-lg hover:bg-hig-bg-tertiary transition-colors">
                                <ChevronDown size={14} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            </button>
                        </div>
                    </div>

                    {/* Expanded details */}
                    {isExpanded && (
                        <div className="mt-4 pt-4 border-t border-hig-divider space-y-4 animate-fade-in">
                            <p className="text-sm text-hig-text-secondary">{merge.taskDescription}</p>

                            {/* Participants */}
                            <div>
                                <p className="text-xs font-semibold text-hig-text-tertiary uppercase tracking-wider mb-2">Participants</p>
                                <div className="flex flex-wrap gap-2">
                                    {merge.participants.map(p => (
                                        <div key={p.replicaId}
                                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${p.consent === 'approved'
                                                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                                : p.consent === 'rejected'
                                                    ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                                                    : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                                                }`}>
                                            {p.consent === 'approved' ? <CheckCircle size={12} /> : p.consent === 'rejected' ? <XCircle size={12} /> : <Clock size={12} />}
                                            {p.name}
                                            <span className="opacity-60 uppercase text-[10px]">{p.consent}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Metrics (if active or completed) */}
                            {(merge.status === 'active' || merge.status === 'completed') && (
                                <div className="grid grid-cols-4 gap-3">
                                    <div className="bg-hig-bg-tertiary/50 rounded-lg p-3">
                                        <p className="text-[10px] text-hig-text-tertiary uppercase tracking-wider mb-1">Nodes Inherited</p>
                                        <p className="text-lg font-bold text-hig-text-primary">{merge.nodesInherited}</p>
                                    </div>
                                    <div className="bg-hig-bg-tertiary/50 rounded-lg p-3">
                                        <p className="text-[10px] text-hig-text-tertiary uppercase tracking-wider mb-1">Actions</p>
                                        <p className="text-lg font-bold text-hig-text-primary">{merge.actionsExecuted}</p>
                                    </div>
                                    <div className="bg-hig-bg-tertiary/50 rounded-lg p-3">
                                        <p className="text-[10px] text-hig-text-tertiary uppercase tracking-wider mb-1">Duration</p>
                                        <p className="text-lg font-bold text-hig-text-primary">{merge.maxDurationMinutes}m</p>
                                    </div>
                                    <div className="bg-hig-bg-tertiary/50 rounded-lg p-3">
                                        <p className="text-[10px] text-hig-text-tertiary uppercase tracking-wider mb-1">Governance</p>
                                        <p className="text-lg font-bold text-emerald-400">{merge.governanceHealth}%</p>
                                    </div>
                                </div>
                            )}

                            {/* Invariants */}
                            <div className="bg-hig-bg-tertiary/30 rounded-xl p-3 border border-hig-divider">
                                <p className="text-[10px] font-semibold text-hig-text-tertiary uppercase tracking-wider mb-2">Merge Invariants</p>
                                <div className="grid grid-cols-3 gap-2 text-xs">
                                    <div className="flex items-center gap-1.5 text-emerald-400">
                                        <Shield size={12} /> No permanent data transfer
                                    </div>
                                    <div className="flex items-center gap-1.5 text-emerald-400">
                                        <Lock size={12} /> Most restrictive governance
                                    </div>
                                    <div className="flex items-center gap-1.5 text-emerald-400">
                                        <CheckCircle size={12} /> Guaranteed separation
                                    </div>
                                </div>
                            </div>

                            {merge.startedAt && (
                                <p className="text-xs text-hig-text-tertiary">
                                    Started: {formatDate(merge.startedAt)}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // ═══════════════════════════════════════════════════════════════════
    // INITIATE MERGE MODAL
    // ═══════════════════════════════════════════════════════════════════
    const InitiateModal = () => (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowInitiateModal(false)}>
            <div className="w-full max-w-xl bg-hig-bg-secondary rounded-2xl border border-purple-500/20 shadow-2xl overflow-hidden max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b border-hig-divider bg-purple-500/5">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                                <GitMerge size={20} className="text-purple-400" />
                            </div>
                            <div>
                                <h3 className="font-bold text-hig-text-primary">Initiate Cognitive Merge</h3>
                                <p className="text-xs text-hig-text-secondary">BREV-009: Temporary Cognitive Fusion</p>
                            </div>
                        </div>
                        <button onClick={() => setShowInitiateModal(false)}
                            className="p-2 text-hig-text-tertiary hover:text-hig-text-primary rounded-lg hover:bg-hig-bg-tertiary transition-colors">
                            <XCircle size={18} />
                        </button>
                    </div>
                </div>

                <div className="p-6 space-y-5">
                    {/* Task Description */}
                    <div>
                        <label className="block text-xs font-semibold text-hig-text-tertiary uppercase tracking-wider mb-1.5">Task Description *</label>
                        <textarea
                            value={mergeTask}
                            onChange={e => setMergeTask(e.target.value)}
                            placeholder="Describe the collaborative task that requires merged cognitive capabilities..."
                            className="w-full bg-hig-bg-tertiary border border-hig-divider rounded-lg px-3 py-2.5 text-sm text-hig-text-primary placeholder-hig-text-tertiary focus:outline-none focus:ring-1 focus:ring-purple-500 h-24 resize-none"
                        />
                    </div>

                    {/* Select Replicas */}
                    <div>
                        <label className="block text-xs font-semibold text-hig-text-tertiary uppercase tracking-wider mb-1.5">Select Replicas to Merge With</label>
                        <div className="space-y-2 max-h-36 overflow-y-auto">
                            {allReplicas.length === 0 ? (
                                <p className="text-sm text-hig-text-tertiary bg-hig-bg-tertiary rounded-lg p-3">
                                    No replicas available. You need at least one other replica to initiate a merge.
                                </p>
                            ) : (
                                allReplicas.map((replica: any) => {
                                    const isSelected = selectedReplicas.includes(replica.id);
                                    return (
                                        <button
                                            key={replica.id}
                                            onClick={() => {
                                                setSelectedReplicas(prev =>
                                                    isSelected ? prev.filter(id => id !== replica.id) : [...prev, replica.id]
                                                );
                                            }}
                                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${isSelected
                                                ? 'bg-purple-500/10 border border-purple-500/20'
                                                : 'bg-hig-bg-tertiary hover:bg-hig-bg-elevated border border-hig-divider'
                                                }`}
                                        >
                                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-cyan-600 flex items-center justify-center text-white text-xs font-bold">
                                                {replica.name.charAt(0)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-hig-text-primary truncate">{replica.name}</p>
                                                <p className="text-xs text-hig-text-tertiary">{replica.category}</p>
                                            </div>
                                            {isSelected ? <CheckCircle size={16} className="text-purple-400" /> : <div className="w-4 h-4 rounded-full border border-hig-divider" />}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    {/* Duration */}
                    <div>
                        <label className="block text-xs font-semibold text-hig-text-tertiary uppercase tracking-wider mb-1.5">Max Duration</label>
                        <select
                            value={mergeDuration}
                            onChange={e => setMergeDuration(Number(e.target.value))}
                            className="w-full bg-hig-bg-tertiary border border-hig-divider rounded-lg px-3 py-2.5 text-sm text-hig-text-primary focus:outline-none focus:ring-1 focus:ring-purple-500"
                        >
                            <option value={30}>30 minutes</option>
                            <option value={60}>1 hour</option>
                            <option value={120}>2 hours</option>
                            <option value={480}>8 hours</option>
                            <option value={1440}>24 hours (maximum)</option>
                        </select>
                    </div>

                    {/* Data Isolation Policy */}
                    <div>
                        <label className="block text-xs font-semibold text-hig-text-tertiary uppercase tracking-wider mb-2">Data Isolation Policy</label>
                        <div className="space-y-2">
                            {[
                                { key: 'memory', label: 'Share Memory Nodes', desc: 'Allow access to task-relevant memories', checked: shareMemory, toggle: () => setShareMemory(!shareMemory) },
                                { key: 'skills', label: 'Share Skill Patterns', desc: 'Allow use of learned skill patterns', checked: shareSkills, toggle: () => setShareSkills(!shareSkills) },
                                { key: 'identity', label: 'Share Identity Traits', desc: 'Allow access to identity preferences (rarely needed)', checked: shareIdentity, toggle: () => setShareIdentity(!shareIdentity) },
                            ].map(item => (
                                <label key={item.key} className="flex items-center justify-between p-3 bg-hig-bg-tertiary/50 rounded-lg border border-hig-divider hover:bg-hig-bg-tertiary/70 cursor-pointer transition-colors">
                                    <div>
                                        <p className="text-sm font-medium text-hig-text-primary">{item.label}</p>
                                        <p className="text-xs text-hig-text-tertiary">{item.desc}</p>
                                    </div>
                                    <button onClick={item.toggle}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${item.checked ? 'bg-purple-500' : 'bg-hig-bg-tertiary'}`}
                                        role="switch" aria-checked={item.checked}>
                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${item.checked ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Invariant Reminder */}
                    <div className="bg-purple-500/5 rounded-xl p-4 border border-purple-500/20">
                        <p className="text-xs text-hig-text-secondary leading-relaxed">
                            <Shield size={12} className="inline text-purple-400 mr-1" />
                            <strong className="text-hig-text-primary">Merge Invariants:</strong> All participants' guardians must consent. The merged entity inherits the most restrictive governance rules. Separation is guaranteed — even on crash. Zero residual data transfer after completion.
                        </p>
                    </div>
                </div>

                <div className="p-6 border-t border-hig-divider bg-hig-bg-tertiary/30 flex justify-end gap-3">
                    <button onClick={() => setShowInitiateModal(false)}
                        className="px-5 py-2.5 rounded-lg text-sm text-hig-text-secondary hover:text-hig-text-primary transition-colors">
                        Cancel
                    </button>
                    <button
                        disabled={!mergeTask.trim() || selectedReplicas.length === 0}
                        className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-cyan-600 text-white rounded-xl font-semibold text-sm shadow-lg shadow-purple-500/20 disabled:opacity-40 disabled:shadow-none hover:shadow-xl transition-all">
                        <GitMerge size={14} />
                        Request Merge
                    </button>
                </div>
            </div>
        </div>
    );

    // ═══════════════════════════════════════════════════════════════════
    // MAIN RENDER
    // ═══════════════════════════════════════════════════════════════════
    return (
        <div className="max-w-5xl mx-auto pb-12">
            {/* Header */}
            <div className="flex items-center gap-3 mb-8">
                <button onClick={() => onNavigate('dashboard')}
                    className="p-2 rounded-lg text-hig-text-tertiary hover:text-hig-text-primary hover:bg-hig-bg-tertiary transition-colors">
                    <ArrowLeft size={18} />
                </button>
                <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                        <h1 className="text-2xl font-bold text-hig-text-primary tracking-tight">Cognitive Merge</h1>
                        <span className="text-xs px-2.5 py-1 rounded-full bg-gradient-to-r from-purple-500/10 to-cyan-500/10 text-purple-400 border border-purple-500/20 font-bold flex items-center gap-1">
                            <Crown size={10} /> Premium
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-hig-bg-tertiary text-hig-text-tertiary border border-hig-divider font-mono">
                            BREV-009
                        </span>
                    </div>
                    <p className="text-sm text-hig-text-secondary">
                        Temporarily fuse sovereign replicas for collaborative task execution with guaranteed separation.
                    </p>
                </div>
                <button onClick={() => setShowInitiateModal(true)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-cyan-600 text-white rounded-xl text-sm font-semibold shadow-lg shadow-purple-500/20 hover:shadow-xl transition-all flex-shrink-0">
                    <Sparkles size={16} />
                    Initiate Merge
                </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
                <div className="card p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                        <Activity size={20} className="text-cyan-400" />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-hig-text-primary">{DEMO_ACTIVE_MERGES.length}</p>
                        <p className="text-xs text-hig-text-tertiary uppercase tracking-wider">Active</p>
                    </div>
                </div>
                <div className="card p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-amber-500/10 flex items-center justify-center">
                        <Clock size={20} className="text-amber-400" />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-hig-text-primary">{DEMO_INCOMING.length}</p>
                        <p className="text-xs text-hig-text-tertiary uppercase tracking-wider">Incoming</p>
                    </div>
                </div>
                <div className="card p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                        <CheckCircle size={20} className="text-emerald-400" />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-hig-text-primary">{DEMO_HISTORY.filter(m => m.status === 'completed').length}</p>
                        <p className="text-xs text-hig-text-tertiary uppercase tracking-wider">Completed</p>
                    </div>
                </div>
                <div className="card p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                        <Shield size={20} className="text-emerald-400" />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-emerald-400">100%</p>
                        <p className="text-xs text-hig-text-tertiary uppercase tracking-wider">Governance</p>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 p-1 bg-hig-bg-tertiary/50 rounded-xl mb-6 w-fit">
                {[
                    { key: 'active' as const, label: 'Active Merges', count: DEMO_ACTIVE_MERGES.length, icon: Activity },
                    { key: 'incoming' as const, label: 'Incoming', count: DEMO_INCOMING.length, icon: Zap },
                    { key: 'history' as const, label: 'History', count: DEMO_HISTORY.length, icon: Clock },
                ].map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`px-5 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${activeTab === tab.key
                            ? 'bg-hig-bg-secondary text-hig-text-primary shadow-sm'
                            : 'text-hig-text-secondary hover:text-hig-text-primary'
                            }`}
                    >
                        <tab.icon size={14} />
                        {tab.label}
                        {tab.count > 0 && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${activeTab === tab.key ? 'bg-purple-500/10 text-purple-400' : 'bg-hig-bg-tertiary text-hig-text-tertiary'
                                }`}>{tab.count}</span>
                        )}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div className="card overflow-hidden">
                {activeTab === 'active' && (
                    DEMO_ACTIVE_MERGES.length === 0 ? (
                        <div className="p-12 text-center">
                            <div className="w-16 h-16 rounded-full bg-hig-bg-tertiary flex items-center justify-center mx-auto mb-4">
                                <GitMerge size={28} className="text-hig-text-tertiary" />
                            </div>
                            <h3 className="text-lg font-semibold text-hig-text-primary mb-2">No Active Merges</h3>
                            <p className="text-sm text-hig-text-secondary mb-6">Initiate a cognitive merge to fuse replicas for collaborative tasks.</p>
                            <button onClick={() => setShowInitiateModal(true)}
                                className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-cyan-600 text-white rounded-xl font-semibold text-sm shadow-lg shadow-purple-500/20">
                                <Sparkles size={14} /> Initiate Merge
                            </button>
                        </div>
                    ) : (
                        DEMO_ACTIVE_MERGES.map(merge => <MergeCard key={merge.id} merge={merge} />)
                    )
                )}

                {activeTab === 'incoming' && (
                    DEMO_INCOMING.length === 0 ? (
                        <div className="p-12 text-center">
                            <div className="w-16 h-16 rounded-full bg-hig-bg-tertiary flex items-center justify-center mx-auto mb-4">
                                <Zap size={28} className="text-hig-text-tertiary" />
                            </div>
                            <h3 className="text-lg font-semibold text-hig-text-primary mb-2">No Incoming Requests</h3>
                            <p className="text-sm text-hig-text-secondary">You'll see merge requests from other replica owners here.</p>
                        </div>
                    ) : (
                        DEMO_INCOMING.map(merge => <MergeCard key={merge.id} merge={merge} showActions />)
                    )
                )}

                {activeTab === 'history' && (
                    DEMO_HISTORY.length === 0 ? (
                        <div className="p-12 text-center">
                            <div className="w-16 h-16 rounded-full bg-hig-bg-tertiary flex items-center justify-center mx-auto mb-4">
                                <Clock size={28} className="text-hig-text-tertiary" />
                            </div>
                            <h3 className="text-lg font-semibold text-hig-text-primary mb-2">No Merge History</h3>
                            <p className="text-sm text-hig-text-secondary">Completed and past merge sessions will appear here.</p>
                        </div>
                    ) : (
                        DEMO_HISTORY.map(merge => <MergeCard key={merge.id} merge={merge} />)
                    )
                )}
            </div>

            {/* Merge Lifecycle Info */}
            <div className="mt-8 card p-6">
                <h3 className="text-sm font-bold text-hig-text-primary mb-4 flex items-center gap-2">
                    <Layers size={16} className="text-purple-400" />
                    Merge Lifecycle (Patent: HR-BREV-009)
                </h3>
                <div className="flex items-center gap-0 overflow-x-auto pb-2">
                    {[
                        { step: 'Request', color: 'bg-amber-500', desc: 'Owner initiates' },
                        { step: 'Consent', color: 'bg-blue-500', desc: 'Guardians approve' },
                        { step: 'Scope', color: 'bg-indigo-500', desc: 'Define boundaries' },
                        { step: 'Fuse', color: 'bg-purple-500', desc: 'Create context' },
                        { step: 'Execute', color: 'bg-cyan-500', desc: 'Perform tasks' },
                        { step: 'Separate', color: 'bg-emerald-500', desc: 'Revert states' },
                        { step: 'Audit', color: 'bg-emerald-600', desc: 'Complete trail' },
                    ].map((item, i, arr) => (
                        <div key={item.step} className="flex items-center flex-shrink-0">
                            <div className="flex flex-col items-center">
                                <div className={`w-8 h-8 rounded-full ${item.color} flex items-center justify-center text-white text-[10px] font-bold shadow-md`}>
                                    {i + 1}
                                </div>
                                <p className="text-[10px] font-semibold text-hig-text-primary mt-1.5">{item.step}</p>
                                <p className="text-[9px] text-hig-text-tertiary">{item.desc}</p>
                            </div>
                            {i < arr.length - 1 && (
                                <div className="w-8 h-0.5 bg-hig-divider mx-1 mt-[-16px]" />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Initiate Modal */}
            {showInitiateModal && <InitiateModal />}
        </div>
    );
}
